import { css } from '@emotion/react';
import variables from '@/library/assets/scss/abstracts/_exports.module.scss';

export type HtmlEditorStyleParams = {
  disabled: boolean;
  isFocused: boolean;
  isFullEditor: boolean;
  styled: boolean;
  minHeight?: string;
  maxHeight?: string;
};

export const ckEditorStyles = (
  { disabled, isFocused, isFullEditor, styled, minHeight, maxHeight }: HtmlEditorStyleParams,
  hasError: boolean
) => css`
  border-radius: ${variables.borderRadiusBase};

  &.is-disabled {
    pointer-events: auto;
    opacity: 1;

    .ck-editor__editable:not(.ck-editor__nested-editable) {
      pointer-events: none;
      cursor: default;
    }

    .ckeditor__editable-container {
      pointer-events: auto;
    }
  }

  .ck-powered-by {
    display: none !important;
  }

  .ck-toolbar {
    background-color: ${styled ? 'rgba(53, 64, 105, 0.1)' : variables.antiFlashWhite2};
    border: none;
    font-family: 'Gotham Narrow SSm A', 'Gotham Narrow SSm B';
    z-index: 15;
    display: ${disabled ? 'none' : 'flex'};

    &.ck-rounded-corners {
      border-radius: ${styled ? '5px' : '0.25rem 0.25rem 0 0'};
      margin: ${styled ? '0 0 0.5rem 0' : '0.125rem 0.125rem 0 0.125rem'};
      left: ${styled ? '0' : '-0.875rem'};
    }
  }

  .ck-editor__editable:not(.ck-editor__nested-editable) {
    &.ck-focused {
      border: none;
      box-shadow: none;
    }

    &.ck-editor__editable_inline {
      padding: ${isFullEditor ? '1cm 2cm 2cm' : styled ? '0.25in 0.75in' : variables.size8};
      border: none;
      font-family: 'Gotham Narrow SSm A', 'Gotham Narrow SSm B';
      font-size: 0.75rem;
      color: ${variables.primary};
      font-weight: 500;
      /* Container owns min/max height so the toolbar is not counted twice.
         flex-basis auto lets the field grow with pasted content; min-height 0
         lets it shrink and scroll once max-height is reached. */
      flex: ${isFullEditor ? '0 0 auto' : '1 1 auto'};
      min-height: ${isFullEditor ? '29cm' : '0'};
      max-height: none;
      overflow: ${isFullEditor ? 'visible' : 'auto'};
    }
  }

  .ckeditor__editable-container {
    min-height: ${minHeight || 'auto'};
    max-height: ${maxHeight || 'none'};
    border: ${styled
      ? 'none'
      : `1px solid ${
          hasError
            ? variables.red
            : isFocused
              ? variables.inputFocusBorderColor
              : variables.inputBorderColor
        }`};
    border-radius: ${variables.borderRadiusBase};
    box-shadow: ${styled ? '0 10px 40px 0 rgba(0, 0, 0, 0.05)' : 'none'};
    display: flex;
    flex-direction: column;
    overflow: ${isFullEditor ? 'auto' : 'hidden'};

    .ck-toolbar {
      flex: 0 0 auto;
    }

    > div:not(.ck-toolbar) {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
    }
  }

  .document-editor {
    display: flex;
    flex-flow: column nowrap;
    min-width: 0;
  }

  .document-editor__editable-container {
    overflow-x: auto;
    overflow-y: auto;
  }

  .document-editor__editable-container .ck-editor__editable {
    width: 21cm;
    min-height: 29cm;
    padding: 1cm 2cm 2cm;
    border-radius: ${variables.borderRadiusBase};
    background: ${variables.white};
    box-shadow: 0 0 5px hsla(0, 0%, 0%, 0.1);
    margin: 0 auto;
  }

  .ck-placeholder {
    font-weight: ${variables.inputPlaceholderFontWeight} !important;
    font-size: ${variables.inputsFontSize} !important;
    color: ${disabled ? variables.pastelGray : variables.inputPlaceholderColor} !important;

    &::before {
      color: ${disabled ? variables.pastelGray : variables.inputPlaceholderColor} !important;
    }
  }

  .ck-content {
    font-family: 'Gotham Narrow SSm A', 'Gotham Narrow SSm B';
    font-size: 0.75rem;
    color: ${variables.primary};

    p {
      font-weight: 500;
      font-size: 1em;
      margin-bottom: 1em;
    }

    h1 {
      font-size: 2em;
      font-weight: 700;
    }

    h2 {
      font-size: 1.5em;
      font-weight: 700;
      padding-top: 0.342em;
      margin-bottom: 0.142em;
    }

    h3 {
      font-size: 1.17em;
      font-weight: 700;
      padding-top: 0.171em;
      margin-bottom: 0.357em;
    }

    h4 {
      font-size: 1em;
      font-weight: 700;
      padding-top: 0.286em;
      margin-bottom: 0.952em;
    }

    ul,
    ol {
      font-size: 1em;
      font-weight: 300;
    }

    table {
      font-size: 1em;
      font-weight: 300;

      .ck-editor__nested-editable {
        padding: 0 !important;
        box-shadow: none !important;
      }
    }

    .table {
      margin: ${variables.size8} 0;
    }

    blockquote {
      margin-left: calc(2 * var(--ck-spacing-large));
      margin-right: calc(2 * var(--ck-spacing-large));
    }

    .todo-list {
      margin-bottom: 0;

      &__label {
        margin-bottom: 0.5rem;

        > input:before {
          border-color: ${variables.turquoise};
          border-radius: 0;
        }

        > input[checked]:before {
          background: ${variables.turquoise};
          border-color: ${variables.turquoise};
        }
      }
    }

    .image > figcaption {
      width: 17cm;
      min-height: auto;
    }
  }
`;
